import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Play, X, Copy, Loader2, Clapperboard } from 'lucide-react'
import NavbarV2 from '../components/landing-v2/NavbarV2'
import { useTemplates } from '../api/hooks'
import { useCloneTemplate } from '../api/useCloneTemplate'
import { createNadDebug } from '../utils/debugLog'
import type { TemplateSummary } from '../api/types'
import { trackInteraction } from '../analytics/interactionAnalytics'

const nad = createNadDebug('dw:templates-gallery')

// ---------------------------------------------------------------------------
// RFC-0934 P1 — public templates gallery. Cards show the template's END-RESULT
// video (poster + play); clicking opens a fullscreen modal that plays it, with a
// "Use this template" clone CTA (reuses the hardened published-clone flow via
// useCloneTemplate). Logged-out → "Sign in to use". Public route (App.tsx),
// unauthenticated GET /templates. Cover/video are a signed URL or null (never a
// relative path — the backend catalog contract), so null → cover/placeholder.
// ---------------------------------------------------------------------------

function TemplateCard({
  template,
  onOpen,
}: {
  template: TemplateSummary
  onOpen: (t: TemplateSummary) => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hasVideo = !!template.preview_video_url

  // Video-first: the card's main asset is the end-result video (poster = cover);
  // it previews on hover (muted/looped), like the landing gallery. Fall back to
  // the cover image, then a placeholder, when there's no video.
  const previewOnHover = () => {
    const v = videoRef.current
    if (v) v.play().catch(() => {})
  }
  const stopPreview = () => {
    const v = videoRef.current
    if (v) {
      v.pause()
      v.currentTime = 0
    }
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(template)}
      onMouseEnter={hasVideo ? previewOnHover : undefined}
      onMouseLeave={hasVideo ? stopPreview : undefined}
      data-testid="template-card"
      className="group relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] text-left transition-colors hover:border-white/25"
    >
      {hasVideo ? (
        <video
          ref={videoRef}
          src={template.preview_video_url ?? undefined}
          poster={template.cover_thumbnail_url ?? undefined}
          muted
          loop
          playsInline
          preload="metadata"
          className="pointer-events-none h-full w-full object-cover"
          data-testid="template-card-video"
        />
      ) : template.cover_thumbnail_url ? (
        <img
          src={template.cover_thumbnail_url}
          alt={template.name}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <Clapperboard className="h-10 w-10 text-white/25" aria-hidden />
      )}
      {/* Play affordance — the modal plays the full video with sound. */}
      <span className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity group-hover:opacity-100">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-black">
          <Play className="h-5 w-5 translate-x-0.5" aria-hidden />
        </span>
      </span>
      <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/80 to-transparent px-3 pb-2 pt-6 text-sm font-medium text-white">
        {template.name}
      </span>
    </button>
  )
}

function TemplateModal({
  template,
  onClose,
}: {
  template: TemplateSummary
  onClose: () => void
}) {
  const { t } = useTranslation('pages')
  const { isAuthenticated, authLoading, cloneTemplate, isCloning, cloneErrorMessage } =
    useCloneTemplate()

  // Esc-to-close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const cloneable = !!(template.share_token && template.version_slug)
  // While auth is still hydrating, show the optimistic label but keep the CTA
  // disabled — never flash "Sign in to use" (or redirect) at a signed-in user.
  const ctaLabel =
    authLoading || isAuthenticated
      ? isCloning
        ? t('templatesGallery.cloning')
        : t('templatesGallery.useTemplate')
      : t('templatesGallery.signInToUse')
  // Enabled for logged-out (redirects to /login); for logged-in only when the
  // template has a publication to clone (fail-loud on a null token/slug).
  const ctaDisabled = authLoading || (isAuthenticated && (isCloning || !cloneable))

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={template.name}
      data-testid="template-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('templatesGallery.close')}
          data-testid="template-modal-close"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white/80 transition-colors hover:bg-black/70 hover:text-white"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        {/* Main asset: the end-result video, else the cover, else a placeholder.
            The dialog is capped at max-h-[90vh]; the media area shrinks
            (min-h-0 flex-1) and the footer is pinned (shrink-0), so the media
            stays bounded for ANY aspect ratio (incl. portrait 9:16) AND the
            close button + CTA remain visible even on short viewports. Do NOT
            force an aspect-video box with h-full: as a flex item
            (min-height:auto) it grows to a portrait video's tall intrinsic
            height once metadata loads, blowing past the box and hiding the
            chrome. NEVER the InspirationV2 "loadingPreview" branch — a
            video-less template must not show a perpetual fake loading box
            (review finding 1). */}
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-black">
          {template.preview_video_url ? (
            <video
              src={template.preview_video_url}
              controls
              autoPlay
              playsInline
              className="max-h-full max-w-full object-contain"
              data-testid="template-modal-video"
            />
          ) : template.cover_thumbnail_url ? (
            <img
              src={template.cover_thumbnail_url}
              alt={template.name}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <div className="flex w-full flex-col items-center justify-center gap-2 py-20 text-white/40">
              <Clapperboard className="h-10 w-10" aria-hidden />
              <span className="text-sm">{t('templatesGallery.noPreview')}</span>
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 p-4">
          <h2 className="text-lg font-semibold text-white">{template.name}</h2>
          <div className="flex items-center gap-3">
            {cloneErrorMessage && (
              <span role="alert" data-testid="clone-error-message" className="text-xs font-medium text-red-400">
                {cloneErrorMessage}
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                trackInteraction('templates_template_clicked', { action: 'use', is_authenticated: isAuthenticated })
                cloneTemplate(template.share_token, template.version_slug)
              }}
              disabled={ctaDisabled}
              data-testid="use-template-button"
              className="flex items-center gap-1.5 rounded-lg bg-primary/90 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isCloning ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Copy className="h-4 w-4" aria-hidden />
              )}
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function TemplatesGalleryPage() {
  const { t } = useTranslation('pages')
  const { data, isLoading, isError } = useTemplates()
  const [active, setActive] = useState<TemplateSummary | null>(null)

  const templates = data?.data ?? []

  useEffect(() => {
    if (data) nad({ event: 'catalog-loaded', count: data.data.length, total: data.total })
  }, [data])

  const openTemplate = (tpl: TemplateSummary) => {
    nad({ event: 'card-opened', templateId: tpl.id, hasVideo: !!tpl.preview_video_url })
    trackInteraction('templates_template_clicked', { action: 'open' })
    setActive(tpl)
  }

  return (
    <div className="min-h-screen bg-background text-white">
      <NavbarV2 />
      <main className="mx-auto w-full max-w-[1273px] px-4 pb-20 pt-28 sm:px-6 md:px-[84px] md:pt-32">
        <header className="mb-8">
          <h1 className="text-hero font-semibold md:text-display-sm">{t('templatesGallery.title')}</h1>
          <p className="mt-2 max-w-2xl text-white/60">{t('templatesGallery.subtitle')}</p>
        </header>

        {isLoading ? (
          <div
            className="flex items-center gap-2 py-24 text-white/60"
            data-testid="templates-loading"
          >
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            {t('templatesGallery.loading')}
          </div>
        ) : isError ? (
          <div className="py-24 text-white/60" data-testid="templates-error">
            {t('templatesGallery.error')}
          </div>
        ) : templates.length === 0 ? (
          <div className="py-24 text-white/60" data-testid="templates-empty">
            {t('templatesGallery.empty')}
          </div>
        ) : (
          <div
            className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="templates-grid"
          >
            {templates.map((tpl) => (
              <TemplateCard key={tpl.id} template={tpl} onOpen={openTemplate} />
            ))}
          </div>
        )}
      </main>

      {active && <TemplateModal template={active} onClose={() => setActive(null)} />}
    </div>
  )
}

export default TemplatesGalleryPage
